import type { CoverageOption } from '../../../../../api/submissions/submission-coverages.types';

/** What one saved option shows in a cart modal row. */
export type QuoteOptionRow = {
  readonly id: string;
  readonly quoteNumber: string;
  readonly presets: string;
  readonly aggLimit: number;
  readonly deductible: number;
  readonly totalPremium: number | null;
  readonly effectiveDate: string;
  readonly commissionRate?: number;
};

type Terms = {
  presets?: unknown;
  aggregateLimit?: unknown;
  deductible?: unknown;
  effectiveDate?: unknown;
  commissionRate?: unknown;
};

const asNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

export const optionLabel = (option: Pick<CoverageOption, 'optionNumber'>) =>
  `Option ${option.optionNumber}`;

/**
 * The limit and retention a quote of this option will carry. The terms are what the quote request
 * sends, and the backend clears them when a Coverages-tab edit changes the product or the parts, so
 * terms that are present are the truth. Without terms the first part is read.
 */
export const optionLimitAndRetention = (
  option: Pick<CoverageOption, 'terms' | 'coverages'>
): { readonly limit?: number; readonly retention?: number } => {
  const terms = (option.terms ?? {}) as Terms;
  const part = option.coverages[0];
  return {
    limit: asNumber(terms.aggregateLimit) ?? part?.limit?.value ?? undefined,
    retention:
      asNumber(terms.deductible) ?? part?.retention?.value ?? undefined,
  };
};

/** Reads a row from the option's terms, else its first part, as the quote will carry them. */
export const toQuoteOptionRow = (option: CoverageOption): QuoteOptionRow => {
  const terms = (option.terms ?? {}) as Terms;
  const { limit, retention } = optionLimitAndRetention(option);
  const commissionRate = asNumber(terms.commissionRate);
  return {
    id: option.id,
    quoteNumber: optionLabel(option),
    presets: typeof terms.presets === 'string' ? terms.presets : '',
    aggLimit: limit ?? 0,
    deductible: retention ?? 0,
    totalPremium: option.indication?.totalPremium ?? null,
    effectiveDate:
      typeof terms.effectiveDate === 'string' ? terms.effectiveDate : '',
    ...(commissionRate != null && { commissionRate }),
  };
};
